<template lang="pug">
div
  v-checkbox.mt-0(
    v-model='include'
    :label=`$t('TENANT_PIPELINE.INCLUDE_NATIVE_AOAI')`
    :disabled='busy'
    color='#18bedf'
    hide-details
  )
  .pl-8.mt-1(v-if='include' style='font-size: 12px; color:#888888;')
    span(v-if='source.native_aoai_error' style='color:#e53935;') {{ source.native_aoai_error }}
    span(v-else) {{ source.native_aoai_entries.map(e => e.display_name || e.key).join('、') }}
  v-card.mt-3.pa-3(v-for='(entry, index) in entries' :key='entry.uid' outlined)
    .d-flex.align-center.mb-2
      v-chip(small label) {{ entry.provider === 'vllm' ? 'vLLM' : 'OpenRouter / OpenAI Compatible' }}
      v-spacer
      v-btn(icon small :disabled='busy' @click='removeEntry(index)' :title=`$t('GENERAL.DELETE')`)
        v-icon(size='18') mdi-delete
    v-row(no-gutters)
      v-col.pr-2(cols='6')
        v-text-field(v-model='entry.key' label='key' outlined dense :disabled='busy || entry.locked' :rules='[requiredRule, uniqueKeyRule]' :hint='entry.locked ? $t("TENANT_PIPELINE.KEY_LOCKED") : ""' persistent-hint @input='entry.keyTouched = true')
      v-col.pl-2(cols='6')
        v-text-field(v-model='entry.display_name' :label=`$t('TENANT_PIPELINE.DISPLAY_NAME')` outlined dense :disabled='busy' :rules='[requiredRule]' @input='entry.autoDisplayName = false')
      template(v-if='entry.provider === "openai_compatible"')
        v-col(cols='12')
          v-text-field(v-model='entry.oaic_param.api_base' label='api_base' outlined dense :disabled='busy' :rules='[requiredRule]')
        v-col.pr-2(cols='6')
          v-text-field(v-model='entry.oaic_param.deployment_name' label='deployment_name' placeholder='google/gemma-4-31b' outlined dense :disabled='busy' :rules='[requiredRule]' @input='autoKey(entry)')
        v-col.pl-2(cols='6')
          v-text-field(v-model='entry.oaic_param.api_key' label='api_key' type='password' outlined dense :disabled='busy' :rules='[requiredRule]')
      template(v-else)
        v-col(cols='12')
          v-text-field(v-model='entry.vllm_param.base_url' label='base_url' outlined dense :disabled='busy' :rules='[requiredRule]')
        v-col.pr-2(cols='6')
          v-text-field(v-model='entry.vllm_param.model' label='model' outlined dense :disabled='busy' :rules='[requiredRule]' @input='autoKey(entry)')
        v-col.pl-2(cols='6')
          v-text-field(v-model='entry.vllm_param.api_key_ref' label='api_key_ref' type='password' outlined dense :disabled='busy' :rules='[requiredRule]')
        v-col.pr-2(cols='4')
          v-text-field(v-model='entry.vllm_param.vllm_version' label='vllm_version' placeholder='0.4.3' outlined dense :disabled='busy' :rules='[requiredRule]')
        v-col.pl-2(cols='4')
          v-checkbox.mt-1(v-model='entry.vllm_param.vision_enabled' label='vision_enabled' :disabled='busy' color='#18bedf' hide-details dense)
        v-col.pl-2(cols='4')
          v-checkbox.mt-1(v-model='entry.vllm_param.tool_calling_enabled' label='tool_calling_enabled' :disabled='busy' color='#18bedf' hide-details dense)
      v-col.pr-2(cols='6')
        v-select(v-model='entry.cost_key' :items='source.price_keys' label='cost_key' :hint=`$t('TENANT_PIPELINE.COST_KEY_HINT')` persistent-hint clearable outlined dense :disabled='busy')
      v-col.pl-2(cols='6')
        v-text-field(v-model='entry.context_size' label='context_size' type='number' outlined dense :disabled='busy' :rules='[optionalIntRule]')
      v-col.pr-2(cols='4')
        v-text-field(v-model='entry.toolclear_tokens' label='toolclear_tokens' type='number' outlined dense :disabled='busy' :rules='[optionalIntRule]')
      v-col.px-1(cols='4')
        v-text-field(v-model='entry.thinking_summary_tokens' label='thinking_summary_tokens' type='number' outlined dense :disabled='busy' :rules='[optionalIntRule]')
      v-col.pl-2(cols='4')
        v-text-field(v-model='entry.autocompact_tokens' label='autocompact_tokens' type='number' outlined dense :disabled='busy' :rules='[optionalIntRule]')
  .d-flex.mt-3
    v-btn.mr-2(:ripple='false' outlined small color='#18bedf' :disabled='busy' @click='addEntry("openai_compatible")')
      v-icon.mr-1(size='12') icon-add
      span OpenRouter
    v-btn(:ripple='false' outlined small color='#18bedf' :disabled='busy' @click='addEntry("vllm")')
      v-icon.mr-1(size='12') icon-add
      span vLLM
  v-select.mt-4(
    v-model='defaultKey'
    :items='allKeys'
    :label=`$t('TENANT_PIPELINE.DEFAULT_MODEL')`
    :hint=`$t('TENANT_PIPELINE.DEFAULT_MODEL_HINT')`
    persistent-hint
    outlined
    dense
    clearable
    :disabled='busy || !allKeys.length'
  )
</template>

<script>
import Vue from "vue";
import i18n from '/common/plugins/vue-i18n.js';

// 模型清單編輯：「包含原生 AOAI 模型」整組開關 + OpenRouter／vLLM 條目。
// 結構契約見 crmapp/docs/model_preference_schema.md；最終驗證由後端 validate_record 負責，
// 這裡只擋必填、key 重複與整數格式。
//
// v-model 的值即送往後端的 models 內容：{ include_native_aoai, custom_entries, default_key }
// 另以 emit('valid', Boolean) 告知父層目前是否可送出。

const OPENROUTER_API_BASE = 'https://openrouter.ai/api/v1';
const KEY_SUFFIX = { openai_compatible: 'openrouter', vllm: 'vllm' };
const THRESHOLD_FIELDS = ['toolclear_tokens', 'thinking_summary_tokens', 'autocompact_tokens'];
let uidSeed = 0;

function emptyToNull(value) {
  return value === '' || value === undefined ? null : value;
}

function toIntOrNull(value) {
  return value === '' || value === null || value === undefined ? null : Number(value);
}

export default Vue.extend({
  props: {
    // 後端 _model_list_state() 的回傳
    source: { type: Object, require: true },
    busy: { type: Boolean, default: false },
  },
  data() {
    return {
      include: this.source.include_native_aoai,
      entries: this.source.custom_entries.map((entry) => this.toEditable(entry, true)),
      defaultKey: this.source.default_key,
      requiredRule: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || i18n.t('RULE.RULE_R'),
      optionalIntRule: (v) => v === null || v === '' || v === undefined || /^\d+$/.test(String(v)) || i18n.t('RULE.RULE_ALPHA_NUMERIC'),
      uniqueKeyRule: (v) => this.allKeys.filter((key) => key === v).length <= 1 || i18n.t('TENANT_PIPELINE.KEY_DUPLICATED'),
    };
  },
  computed: {
    allKeys() {
      const native = this.include ? this.source.native_aoai_entries.map((e) => e.key) : [];
      return native.concat(this.entries.map((e) => e.key).filter((key) => key));
    },
    payload() {
      return {
        include_native_aoai: this.include,
        custom_entries: this.entries.map((entry) => this.toPayload(entry)),
        default_key: this.allKeys.includes(this.defaultKey) ? this.defaultKey : null,
      };
    },
    valid() {
      const keys = this.allKeys;
      if (!keys.length || new Set(keys).size !== keys.length) {
        return false;
      }
      return this.entries.every((entry) => {
        const block = entry.provider === 'vllm' ? entry.vllm_param : entry.oaic_param;
        const required = entry.provider === 'vllm'
          ? ['base_url', 'api_key_ref', 'model', 'vllm_version']
          : ['api_base', 'api_key', 'deployment_name'];
        const ints = ['context_size'].concat(THRESHOLD_FIELDS);
        return !!entry.key && !!entry.display_name
          && required.every((field) => block[field] && String(block[field]).trim() !== '')
          && ints.every((field) => this.optionalIntRule(entry[field]) === true);
      });
    },
  },
  watch: {
    payload: {
      immediate: true,
      handler(val) {
        this.$emit('input', val);
      },
    },
    valid: {
      immediate: true,
      handler(val) {
        this.$emit('valid', val);
      },
    },
  },
  methods: {
    toEditable(entry, locked) {
      uidSeed += 1;
      const editable = {
        uid: uidSeed,
        locked: locked,
        keyTouched: locked,
        autoDisplayName: !locked,
        key: entry.key || '',
        provider: entry.provider,
        display_name: entry.display_name || '',
        cost_key: entry.cost_key || null,
        context_size: entry.context_size === null || entry.context_size === undefined ? '' : entry.context_size,
        oaic_param: Object.assign({ api_base: OPENROUTER_API_BASE, api_key: '', deployment_name: '' }, entry.oaic_param || {}),
        vllm_param: Object.assign({ base_url: '', api_key_ref: '', model: '', vllm_version: '', vision_enabled: false, tool_calling_enabled: true }, entry.vllm_param || {}),
      };
      THRESHOLD_FIELDS.forEach((field) => {
        editable[field] = entry[field] === null || entry[field] === undefined ? '' : entry[field];
      });
      return editable;
    },
    toPayload(entry) {
      const result = {
        key: entry.key.trim(),
        provider: entry.provider,
        display_name: entry.display_name.trim(),
        cost_key: emptyToNull(entry.cost_key),
        context_size: toIntOrNull(entry.context_size),
      };
      THRESHOLD_FIELDS.forEach((field) => {
        result[field] = toIntOrNull(entry[field]);
      });
      if (entry.provider === 'vllm') {
        result.vllm_param = Object.assign({}, entry.vllm_param);
      } else {
        result.oaic_param = Object.assign({}, entry.oaic_param);
      }
      return result;
    },
    addEntry(provider) {
      this.entries.push(this.toEditable({ provider: provider }, false));
    },
    removeEntry(index) {
      this.entries.splice(index, 1);
    },
    autoKey(entry) {
      // 慣例 {model}_{provider}；使用者手動改過 key 後不再覆寫，既有條目的 key 永不變更
      if (entry.locked || (entry.keyTouched && entry.key)) {
        return;
      }
      const model = entry.provider === 'vllm' ? entry.vllm_param.model : entry.oaic_param.deployment_name;
      const base = String(model || '').split('/').pop().toLowerCase().replace(/[^a-z0-9.-]+/g, '-').replace(/^-+|-+$/g, '');
      entry.key = base ? `${base}_${KEY_SUFFIX[entry.provider]}` : '';
      entry.keyTouched = false;
      if (entry.autoDisplayName) {
        entry.display_name = String(model || '').split('/').pop();
      }
    },
  },
});
</script>
